import { Button, Card, CardBody, Select, Spinner } from '@foundation/ui'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import styles from './survey-results.module.css'
import { AppLayout } from '@/components/AppLayout'
import { api, type SurveyResults } from '@/lib/api'
import { isAuthenticated } from '@/lib/auth-store'
import { toast } from '@/lib/toast'

export const Route = createFileRoute('/survey-results')({
  beforeLoad: () => {
    if (!isAuthenticated()) {
      throw redirect({ to: '/login' })
    }
  },
  component: SurveyResultsPage,
})

const PERIOD_OPTIONS = [
  { value: '', label: 'All Time' },
  { value: '7d', label: 'Last 7 Days' },
  { value: '30d', label: 'Last 30 Days' },
  { value: '90d', label: 'Last 90 Days' },
]

const REASON_COLORS: Record<string, string> = {
  price: styles.barPrice ?? '',
  too_expensive: styles.barPrice ?? '',
  missing_features: styles.barFeatures ?? '',
  features: styles.barFeatures ?? '',
  competitor: styles.barCompetitor ?? '',
  switched_competitor: styles.barCompetitor ?? '',
  support: styles.barSupport ?? '',
  poor_support: styles.barSupport ?? '',
  other: styles.barOther ?? '',
}

function getBarClass(reason: string): string {
  const normalized = reason.toLowerCase().replace(/\s+/g, '_')
  return REASON_COLORS[normalized] ?? ''
}

function SurveyResultsPage() {
  const [results, setResults] = useState<SurveyResults | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [period, setPeriod] = useState('')

  useEffect(() => {
    let cancelled = false
    api
      .getSurveyResults(period || undefined)
      .then((res) => {
        if (!cancelled) setResults(res.data)
      })
      .catch((err: unknown) => {
        if (!cancelled)
          toast.error(err instanceof Error ? err.message : 'Failed to load survey results')
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [period])

  if (isLoading) {
    return (
      <AppLayout title="Survey Results">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <Spinner size="lg" />
        </div>
      </AppLayout>
    )
  }

  if (!results || (results.total_responses === 0 && results.reasons.length === 0)) {
    return (
      <AppLayout title="Survey Results">
        <Card className={styles.emptyState ?? ''}>
          <CardBody>
            <div className={styles.emptyIllustration ?? ''}>
              <svg
                width="120"
                height="100"
                viewBox="0 0 120 100"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="20"
                  y="10"
                  width="80"
                  height="80"
                  rx="6"
                  stroke="currentColor"
                  strokeWidth="2"
                  opacity="0.2"
                />
                <line
                  x1="35"
                  y1="30"
                  x2="85"
                  y2="30"
                  stroke="currentColor"
                  strokeWidth="2"
                  opacity="0.15"
                  strokeLinecap="round"
                />
                <line
                  x1="35"
                  y1="42"
                  x2="75"
                  y2="42"
                  stroke="currentColor"
                  strokeWidth="2"
                  opacity="0.15"
                  strokeLinecap="round"
                />
                <line
                  x1="35"
                  y1="54"
                  x2="80"
                  y2="54"
                  stroke="currentColor"
                  strokeWidth="2"
                  opacity="0.15"
                  strokeLinecap="round"
                />
                <line
                  x1="35"
                  y1="66"
                  x2="65"
                  y2="66"
                  stroke="currentColor"
                  strokeWidth="2"
                  opacity="0.15"
                  strokeLinecap="round"
                />
                <circle cx="90" cy="70" r="20" fill="var(--color-primary)" opacity="0.1" />
                <path
                  d="M82 70L88 76L98 64"
                  stroke="var(--color-primary)"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity="0.5"
                />
              </svg>
            </div>
            <h3 className={styles.emptyTitle ?? ''}>No survey responses yet</h3>
            <p className={styles.emptyDescription ?? ''}>
              When cancelled subscribers complete your cancellation survey, their responses will be
              aggregated here with visual breakdowns by reason. Understanding why customers leave
              helps you improve retention.
            </p>
            <div className={styles.emptyHints ?? ''}>
              <div className={styles.emptyHint ?? ''}>
                Include a survey link in your win-back sequence emails
              </div>
              <div className={styles.emptyHint ?? ''}>
                Responses are grouped by reason with percentage breakdowns
              </div>
              <div className={styles.emptyHint ?? ''}>
                Written feedback from customers is displayed separately
              </div>
            </div>
          </CardBody>
        </Card>
      </AppLayout>
    )
  }

  const topReason = results.reasons.length > 0 ? results.reasons[0] : null
  const uniqueReasons = results.reasons.length

  return (
    <AppLayout title="Survey Results">
      {/* Header with filters */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.totalResponses}>
            {results.total_responses} total response{results.total_responses !== 1 ? 's' : ''}
          </span>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <div className={styles.filterSelect}>
            <Select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              options={PERIOD_OPTIONS}
            />
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => toast.info('Export functionality coming soon')}
          >
            Export CSV
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className={styles.statsSummary}>
        <Card className={styles.summaryCard}>
          <CardBody>
            <div className={styles.summaryValue}>{results.total_responses}</div>
            <div className={styles.summaryLabel}>Total Responses</div>
          </CardBody>
        </Card>
        <Card className={styles.summaryCard}>
          <CardBody>
            <div className={styles.summaryValue}>{uniqueReasons}</div>
            <div className={styles.summaryLabel}>Unique Reasons</div>
          </CardBody>
        </Card>
        <Card className={styles.summaryCard}>
          <CardBody>
            <div className={styles.summaryValue}>
              {topReason ? `${topReason.percentage.toFixed(0)}%` : '--'}
            </div>
            <div className={styles.summaryLabel}>Top Reason: {topReason?.reason ?? 'N/A'}</div>
          </CardBody>
        </Card>
      </div>

      {/* Reason Distribution */}
      <div className={styles.sectionTitle}>Cancellation Reasons</div>
      <div className={styles.reasonsGrid}>
        {results.reasons.map((reason) => (
          <Card key={reason.reason} className={styles.reasonCard}>
            <CardBody>
              <div className={styles.reasonHeader}>
                <span className={styles.reasonLabel}>{reason.reason}</span>
                <span className={styles.reasonCount}>
                  {reason.count} response{reason.count !== 1 ? 's' : ''}
                </span>
              </div>
              <div className={styles.reasonBarContainer}>
                <div
                  className={`${styles.reasonBar} ${getBarClass(reason.reason)}`}
                  style={{ width: `${reason.percentage}%` }}
                />
              </div>
              <span className={styles.reasonPercentage}>{reason.percentage.toFixed(1)}%</span>
            </CardBody>
          </Card>
        ))}
      </div>

      {/* Freeform Responses */}
      {results.freeform_responses.length > 0 && (
        <div className={styles.freeformSection}>
          <h3 className={styles.sectionTitle}>
            Written Feedback ({results.freeform_responses.length})
          </h3>
          <div className={styles.freeformList}>
            {results.freeform_responses.map((response) => (
              <div key={response.cancellationId} className={styles.freeformItem}>
                <p className={styles.freeformText}>&ldquo;{response.text}&rdquo;</p>
                <span className={styles.freeformMeta}>
                  {new Date(response.createdAt).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </AppLayout>
  )
}
